/**
 * useViewTransition — Reusable wrapper around the View Transitions API.
 *
 * Usage:
 *   const { startTransition, isTransitioning } = useViewTransition();
 *   startTransition(() => { navigate('/somewhere'); });
 *
 * Falls back to immediate execution when the API isn't available.
 * Designed for app-wide adoption — not billing-specific.
 */
import { useCallback, useRef, useState } from 'react';

/** Feature-detect once at module level */
const supportsViewTransitions =
  typeof document !== 'undefined' &&
  typeof (document as any).startViewTransition === 'function';

export interface UseViewTransitionReturn {
  /** Wrap a DOM mutation (e.g. navigation) in a view transition */
  startTransition: (callback: () => void) => void;
  /** True while a transition is in flight */
  isTransitioning: boolean;
  /** Whether the browser supports the View Transitions API */
  isSupported: boolean;
}

export function useViewTransition(): UseViewTransitionReturn {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const activeRef = useRef<any>(null);

  const startTransition = useCallback((callback: () => void) => {
    // Skip if already mid-transition (prevents double-fire)
    if (activeRef.current) {
      callback();
      return;
    }

    if (!supportsViewTransitions) {
      callback();
      return;
    }

    setIsTransitioning(true);
    const transition = (document as any).startViewTransition(callback);
    activeRef.current = transition;

    transition.finished.finally(() => {
      setIsTransitioning(false);
      activeRef.current = null;
    });
  }, []);

  return { startTransition, isTransitioning, isSupported: supportsViewTransitions };
}
